import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import SubnetService from "../../service/SubnetService";
import ThemeService from "../../service/ThemeService";
import { THEME_DEFAULTS } from "../../constants/themeDefaults";
import { resolveThemeImageUrl } from "../../utilities/resolveThemeImageUrl";
import {
  deriveBorderFromBackground,
  deriveTextMuted,
  deriveColorDark,
  deriveColorLight,
} from "../../utilities/themeColorUtils";

export const ThemeContext = createContext(null);

const subnetService = new SubnetService();
const themeService = new ThemeService();

const DEFAULT_FAVICON = `${process.env.PUBLIC_URL}/assets/icons/favicon-96x96.png`;

const applyThemeVariables = (theme) => {
  const root = document.documentElement;
  const primary = theme?.colors?.primary?.main ?? THEME_DEFAULTS.primary;
  const secondary = theme?.colors?.secondary ?? THEME_DEFAULTS.secondary;
  const background = theme?.colors?.background?.page ?? THEME_DEFAULTS.backgroundPage;
  const text = theme?.colors?.text?.body ?? THEME_DEFAULTS.textBody;
  const fontFamily = theme?.typography?.fontFamilyName ?? THEME_DEFAULTS.fontFamily;

  const textMuted =
    theme?.colors?.text?.muted ?? deriveTextMuted(text, background);
  const border =
    theme?.colors?.border ?? deriveBorderFromBackground(background);
  const primaryDark = deriveColorDark(primary);
  const primaryLight = deriveColorLight(primary);
  const secondaryDark = deriveColorDark(secondary);
  const secondaryLight = deriveColorLight(secondary);

  root.style.setProperty("--theme-primary", primary);
  root.style.setProperty("--theme-secondary", secondary);
  root.style.setProperty("--theme-background", background);
  root.style.setProperty("--theme-text", text);
  root.style.setProperty("--theme-text-muted", textMuted);
  root.style.setProperty("--theme-border", border);
  root.style.setProperty("--theme-primary-dark", primaryDark);
  root.style.setProperty("--theme-primary-light", primaryLight);
  root.style.setProperty("--theme-secondary-dark", secondaryDark);
  root.style.setProperty("--theme-secondary-light", secondaryLight);
  root.style.setProperty("--theme-font-family", `"${fontFamily}", sans-serif`);
};

const resetThemeVariables = () => {
  const root = document.documentElement;
  const primary = THEME_DEFAULTS.primary;
  const secondary = THEME_DEFAULTS.secondary;
  const background = THEME_DEFAULTS.backgroundPage;
  const text = THEME_DEFAULTS.textBody;

  root.style.setProperty("--theme-primary", primary);
  root.style.setProperty("--theme-secondary", secondary);
  root.style.setProperty("--theme-background", background);
  root.style.setProperty("--theme-text", text);
  root.style.setProperty("--theme-text-muted", deriveTextMuted(text, background));
  root.style.setProperty("--theme-border", deriveBorderFromBackground(background));
  root.style.setProperty("--theme-primary-dark", deriveColorDark(primary));
  root.style.setProperty("--theme-primary-light", deriveColorLight(primary));
  root.style.setProperty("--theme-secondary-dark", deriveColorDark(secondary));
  root.style.setProperty("--theme-secondary-light", deriveColorLight(secondary));
  root.style.setProperty("--theme-font-family", `"${THEME_DEFAULTS.fontFamily}", sans-serif`);
};

const applyFavicon = (url) => {
  const href = url || DEFAULT_FAVICON;
  const iconLinks = document.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"]');
  if (iconLinks.length > 0) {
    iconLinks.forEach((link) => {
      link.href = href;
    });
  } else {
    const link = document.createElement("link");
    link.rel = "icon";
    link.href = href;
    document.head.appendChild(link);
  }
};

/** API pode retornar theme aninhado em .theme ou plano */
const getThemePayload = (data) => data?.theme ?? data;

export const ThemeProvider = ({ children, userInfo }) => {
  const [subnetInfo, setSubnetInfo] = useState(null);
  const [theme, setTheme] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const isSubnetLeader = subnetInfo && userInfo?.id != null && Number(subnetInfo.ownerId) === Number(userInfo.id);

  const refreshSubnet = useCallback(async () => {
    const token = localStorage.getItem("__extraconomy_token");
    if (!token) return null;
    try {
      const result = await subnetService.getBelongsToSubnet(token);
      setSubnetInfo(result);
      return result;
    } catch (err) {
      setError(err);
      setSubnetInfo(null);
      return null;
    }
  }, []);

  const refreshTheme = useCallback(async () => {
    const token = localStorage.getItem("__extraconomy_token");
    if (!token) return null;
    try {
      const res = await themeService.getThemeByUser(token);
      const themeData = res?.result ?? null;
      setTheme(themeData);
      return themeData;
    } catch (err) {
      setError(err);
      setTheme(null);
      return null;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem("__extraconomy_token");
    if (!token) {
      setIsLoading(false);
      return;
    }
    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const subnet = await subnetService.getBelongsToSubnet(token);
        if (cancelled) return;
        setSubnetInfo(subnet);
        if (subnet) {
          const res = await themeService.getThemeByUser(token);
          if (cancelled) return;
          const themeData = res?.result ?? null;
          setTheme(themeData);
        } else {
          setTheme(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err);
          setSubnetInfo(null);
          setTheme(null);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (subnetInfo && theme) {
      const payload = getThemePayload(theme);
      applyThemeVariables(payload);
      if (payload?.assets?.faviconUrl) {
        applyFavicon(resolveThemeImageUrl(payload.assets.faviconUrl) || payload.assets.faviconUrl);
      } else {
        applyFavicon(DEFAULT_FAVICON);
      }
    } else {
      resetThemeVariables();
      applyFavicon(DEFAULT_FAVICON);
    }
  }, [theme, subnetInfo]);

  const value = {
    subnetInfo,
    isSubnetLeader,
    theme,
    isLoading,
    error,
    refreshTheme,
    refreshSubnet,
    effectiveTheme: theme ?? null,
    logoUrl: (() => {
      const payload = getThemePayload(theme);
      const rawLogo = payload?.assets?.logoUrl;
      return (rawLogo && resolveThemeImageUrl(rawLogo)) || THEME_DEFAULTS.logoUrl;
    })(),
    // Logo do celular (Federnet: "Logo del Marketplace (cellulare)"); null = usar a logoUrl
    logoSmallUrl: (() => {
      const rawLogo = getThemePayload(theme)?.assets?.logoSmallUrl;
      return (rawLogo && resolveThemeImageUrl(rawLogo)) || null;
    })(),
  };

  if (isLoading) {
    return (
      <ThemeContext.Provider value={value}>
        <div
          style={{
            position: "fixed",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#f8f9fa",
            zIndex: 9999,
          }}
        >
          <i className="pi pi-spin pi-spinner text-6xl text-500" />
        </div>
      </ThemeContext.Provider>
    );
  }

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useThemeContext = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    return {
      subnetInfo: null,
      isSubnetLeader: false,
      theme: null,
      isLoading: false,
      error: null,
      refreshTheme: () => {},
      refreshSubnet: () => {},
      effectiveTheme: null,
      logoUrl: THEME_DEFAULTS.logoUrl,
      logoSmallUrl: null,
    };
  }
  return ctx;
};
